import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageSquare, Phone, Filter, ChevronRight } from "lucide-react";
import { getSessions, getSession, apiErr } from "../lib/api";
import { PageHead, Stat, Modal, Empty, Pill } from "./_ui";

function fmt(t) {
  if (!t) return "—";
  try { return new Date(t).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return t; }
}

export default function Sessions() {
  const [data, setData] = useState({ sessions: [], total: 0, withContact: 0 });
  const [onlyContact, setOnlyContact] = useState(false);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = () => { setLoading(true); getSessions(onlyContact).then(setData).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false)); };
  useEffect(load, [onlyContact]); // eslint-disable-line

  const view = async (sid) => {
    try { const d = await getSession(sid); setOpen(d); } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-7 md:px-8" data-testid="admin-sessions">
      <PageHead eyebrow="Visitor intelligence" title="Sessions"
        desc="Every website assistant conversation, with the visitors who shared a phone number or email flagged for follow-up." />

      <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Stat icon={MessageSquare} label="Total sessions" value={data.total} sub="All chatbot conversations" />
        <Stat icon={Phone} tint={{ bg: "#E6F0EE", fg: "#2F7A6B" }} label="Shared contact" value={data.withContact} sub="Leads to follow up" />
        <Stat icon={Filter} tint={{ bg: "#F0ECF7", fg: "#6A4EA8" }} label="Showing" value={data.sessions.length} sub={onlyContact ? "Contact-shared only" : "All sessions"} />
      </div>

      <div className="mt-6 flex items-center justify-between">
        <p className="font-body text-sm font-semibold text-[#2C241B]">{loading ? "Loading…" : `${data.sessions.length} sessions`}</p>
        <button onClick={() => setOnlyContact((v) => !v)} data-testid="sessions-filter-contact"
          className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 font-body text-xs font-semibold ${onlyContact ? "border-[#2F7A6B] bg-[#E6F0EE] text-[#2F7A6B]" : "border-[#E5DFD3] text-[#5C5449] hover:bg-[#F5F1E8]"}`}>
          <Phone size={14} /> {onlyContact ? "Showing leads" : "Only leads"}
        </button>
      </div>

      <div className="mt-3 space-y-2">
        {data.sessions.map((s) => (
          <button key={s.session_id} data-testid={`session-row-${s.session_id}`} onClick={() => view(s.session_id)}
            className="flex w-full items-center justify-between gap-3 rounded-xl border border-[#E9E2D6] bg-white p-4 text-left transition-colors hover:border-[#E0A98F]">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-body text-sm font-semibold text-[#2C241B]">{s.message_count || 0} messages</span>
                {s.has_contact && <span className="inline-flex items-center gap-1 rounded-full bg-[#E6F0EE] px-2 py-0.5 font-body text-[11px] font-semibold text-[#2F7A6B]"><Phone size={11} /> lead</span>}
                {s.page && <span className="font-body text-xs text-[#A79C8A]">from {s.page}</span>}
              </div>
              <p className="mt-1 truncate font-body text-xs text-[#8A8072]">
                {(s.contacts && s.contacts.length) ? `Contact: ${s.contacts.join(", ")} · ` : ""}Last active {fmt(s.last_at)}
              </p>
            </div>
            <ChevronRight size={18} className="shrink-0 text-[#C7BEAF]" />
          </button>
        ))}
        {!loading && data.sessions.length === 0 && <Empty>No sessions yet — conversations from the website assistant will appear here.</Empty>}
      </div>

      {open && (
        <Modal title="Conversation" onClose={() => setOpen(null)} testid="session-modal">
          <div className="mb-3 flex flex-wrap items-center gap-2 font-body text-xs text-[#8A8072]">
            {open.session?.has_contact && <Pill status="active" />}
            {open.session?.contacts?.length ? <span>Contact: <b className="text-[#2C241B]">{open.session.contacts.join(", ")}</b></span> : <span>No contact shared</span>}
          </div>
          <div className="max-h-[55vh] space-y-2 overflow-y-auto">
            {(open.messages || []).map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3 py-2 font-body text-sm ${m.role === "user" ? "bg-[#D84315] text-white" : "border border-[#E9E2D6] bg-[#FAFAF5] text-[#2C241B]"}`}>{m.content}</div>
              </div>
            ))}
            {(open.messages || []).length === 0 && <Empty>No messages stored for this session.</Empty>}
          </div>
        </Modal>
      )}
    </div>
  );
}
